import { ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Edit as EditIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  useAddAttachmentToNode,
  useGetAllAttachmentForNode,
  useGetNode,
  useRemoveAttachmentFromNode,
} from '../hooks/api/use-ontology.hook';
import { NodeType } from '../../../types/constants/node-type';
import {
  NodeApiModel,
  NodeRelationship,
  WithFeatureType,
  WithPointLayer,
  WithBreaklineLayer,
  WithDomains as WithNodeDomains,
  WithParent,
  WithSymbol,
} from '../../../types/models/node.model';
import { Attachment } from '../components/Attachment/Attachment';
import { useUser } from '../contexts/user.context';
import { Permission } from '../../../types/constants/permission';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { Attributes } from '../components/Ontology/Attributes';

export const OntologyNodeDetails = () => {
  const navigate = useNavigate();
  const { nodeType, nodeId } = useParams<{ nodeType: NodeType; nodeId: string }>();
  const [user] = useUser();

  const { loading, error, node } = useGetNode<
    NodeApiModel & WithParent & WithNodeDomains & WithPointLayer & WithBreaklineLayer & WithSymbol & WithFeatureType
  >({
    nodeId: Number(nodeId),
    nodeType: nodeType as NodeType,
    include: [
      NodeRelationship.PARENT,
      NodeRelationship.ATTRIBUTES,
      NodeRelationship.DOMAINS,
      NodeRelationship.POINT_LAYER,
      NodeRelationship.BREAKLINE_LAYER,
      NodeRelationship.SYMBOL,
      NodeRelationship.FEATURE_TYPES,
    ],
  });

  const [addAttachmentToNode, { loading: addAttachmentToNodeLoading, error: addAttachmentToNodeError }] =
    useAddAttachmentToNode();
  const [removeAttachmentFromNode, { loading: removeAttachmentFromNodeLoading, error: removeAttachmentFromNodeError }] =
    useRemoveAttachmentFromNode();
  const { loading: getAllAttachmentsForNodeLoading, attachments } = useGetAllAttachmentForNode({
    nodeId: Number(nodeId),
    nodeType: nodeType as NodeType,
  });
  const { loading: getAllAttachmentsForParentLoading, attachments: parentAttachments } = useGetAllAttachmentForNode({
    nodeId: Number(node?.parent?.id),
    nodeType: node?.parent?.type as NodeType,
  });

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!node || !e.target.files?.[0]) {
      return;
    }

    const file = e.target.files[0];
    await addAttachmentToNode({ nodeId: node.id, nodeType: node.type, attachment: file });
  };

  const renderAttachments = () => {
    if (!node) {
      return null;
    }

    return getAllAttachmentsForNodeLoading || getAllAttachmentsForParentLoading ? (
      <LoadingIndicator />
    ) : (
      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
        {attachments?.map((image) => (
          <Attachment
            key={`${node.id}-${image}`}
            url={image}
            deletionCallback={async () => {
              await removeAttachmentFromNode({ nodeId: node.id, nodeType: node.type, attachment: image });
            }}
          />
        ))}
        {node.parent
          ? parentAttachments?.map((image) => (
              <Attachment
                key={`${node.parent!.id}-${image}`}
                url={image}
                deletionCallback={async () => {
                  await removeAttachmentFromNode({
                    nodeId: node.parent!.id,
                    nodeType: node.parent!.type,
                    attachment: image,
                  });
                }}
              />
            ))
          : null}
      </Box>
    );
  };

  const renderNodeProperties = () => {
    if (!node) {
      return null;
    }

    return (
      <>
        <TableRow>
          <TableCell>
            <b>Associated Domains:</b>
          </TableCell>
          <TableCell align="left">
            <Stack direction="row" spacing={1}>
              {node.domains
                .sort((a, b) => (a.shortName! > b.shortName! ? 1 : -1))
                .map((item) => (
                  <Chip
                    key={item.id}
                    label={item.shortName}
                    color={user?.domains.find((domain) => domain.id === item.id) ? 'primary' : 'error'}
                    variant="outlined"
                  />
                ))}
            </Stack>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <Box display="flex" flexDirection="row">
              <b>Photos:</b>
            </Box>
          </TableCell>
          <TableCell align="left">
            {user?.hasPermission(Permission.ONTOLOGY) ? (
              <>
                <LoadingButton
                  variant="outlined"
                  component="label"
                  loading={addAttachmentToNodeLoading || removeAttachmentFromNodeLoading}
                >
                  Upload
                  <input type="file" hidden onChange={handleFileUpload} />
                </LoadingButton>
                {addAttachmentToNodeError ? (
                  <Alert severity="error">{addAttachmentToNodeError?.response?.data.message}</Alert>
                ) : null}
                {removeAttachmentFromNodeError ? (
                  <Alert severity="error">{removeAttachmentFromNodeError?.response?.data.message}</Alert>
                ) : null}
              </>
            ) : null}
            {renderAttachments()}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <b>Name:</b>
          </TableCell>
          <TableCell align="left">{node.name}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <b>Description:</b>
          </TableCell>
          <TableCell align="left">{node.description || node.parent?.description}</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>
            <b>Include in Surface:</b>
          </TableCell>
          <TableCell align="left">
            <Checkbox checked={node.includeInSurface} disabled />
          </TableCell>
        </TableRow>
        {node.featureType ? (
          <TableRow>
            <TableCell>
              <b>Feature Type:</b>
            </TableCell>
            <TableCell align="left">{node.featureType.name}</TableCell>
          </TableRow>
        ) : null}
        <TableRow>
          <TableCell>
            <b>Point Layer:</b>
          </TableCell>
          <TableCell align="left">{node.pointLayer?.name}</TableCell>
        </TableRow>
        {node.featureType?.name !== 'Point' ? (
          <TableRow>
            <TableCell>
              <b>Linework Layer:</b>
            </TableCell>
            <TableCell align="left">{node.breaklineLayer?.name}</TableCell>
          </TableRow>
        ) : null}
        <TableRow>
          <TableCell>
            <b>Symbol / Line:</b>
          </TableCell>
          <TableCell align="left">{node.symbol?.name}</TableCell>
        </TableRow>
      </>
    );
  };

  const getBreadcrumbTitle = () => {
    if (!node) {
      return '';
    }

    let { parent }: { name: string; parent?: { name: string; parent?: { name: string } } } | undefined = node;
    let title = '';
    while (parent) {
      title = `${parent.name} > ${title}`;
      parent = parent.parent;
    }
    return title + node.name;
  };

  if (loading) {
    return <LoadingIndicator />;
  } else if (error || !node) {
    return <Alert severity="error">{error?.response?.data.message || 'Node not found'}</Alert>;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Helmet title={node.name} />
      <Box display="flex" flexDirection="column" height="100%" sx={{ p: '0 12px' }}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" width="100%" style={{ margin: '10px 0' }}>
          <Box width="100%" display="flex">
            <Typography variant="h5">{getBreadcrumbTitle()}</Typography>
            <Box flexGrow={1} />
            {user?.hasPermission(Permission.ONTOLOGY) ? (
              <Button
                color="primary"
                variant="contained"
                startIcon={<EditIcon />}
                disabled={loading}
                onClick={() => navigate(`/ontology/${node.type}/${node.id}/edit`)}
              >
                Edit
              </Button>
            ) : null}
          </Box>
        </Box>
        <Divider />
        <Box display="flex" flexDirection="column">
          <Table sx={{ minWidth: 550, whiteSpace: 'nowrap' }}>
            <TableBody>{renderNodeProperties()}</TableBody>
          </Table>
          <Attributes />
        </Box>
      </Box>
    </Box>
  );
};
